import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import type { AdminAnalytics, AnalyticsDay } from '../api/adminAnalyticsApi';
import { fmtCalendarDay, fmtInt, fmtPct, fmtRub } from '../model/format';
import { Legend, SERIES_COLORS, TimeChart, type ChartSeries } from './TimeChart';
import { UserDayHeatmap } from './UserDayHeatmap';

const [C1, C2, C3] = SERIES_COLORS;
/** Круги модели — порядковая шкала одного тона (dataviz --ordinal на #1e1e20): чем больше вызовов, тем ярче. */
const [R1, R2, R3] = ['#184f95', '#3987e5', '#86b6ef'];

export function Panel({ title, hint, children, className }: { title: string; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn('min-w-0 rounded-[18px] bg-card p-4 shadow-[inset_0_0_0_1px_#2a2a30] sm:p-5', className)}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="text-[14px] font-semibold text-fg">{title}</h3>
        {hint && <div className="text-[12px] text-muted">{hint}</div>}
      </div>
      {children}
    </section>
  );
}

/** Изменение к прошлому такому же периоду: знак и цвет по тому, хорошо ли рост. */
export function Delta({ now, prev, goodUp = true }: { now: number; prev: number; goodUp?: boolean }) {
  // Без базы сравнения процент бессмыслен: при нуле в прошлом периоде дельту не показываем.
  if (!prev) return null;
  const change = (now - prev) / prev;
  const good = change === 0 ? null : change > 0 === goodUp;
  return (
    <span className={cn('tabular-nums', good === true && 'text-success', good === false && 'text-[#f47a7e]', good === null && 'text-muted')}>
      {change > 0 ? '▲' : change < 0 ? '▼' : '='} {fmtPct(Math.abs(change))}
    </span>
  );
}

export function Tile({
  label,
  value,
  sub,
  delta,
  className,
}: {
  label: string;
  value: string;
  sub?: ReactNode;
  delta?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('min-w-0 rounded-[18px] bg-card px-4 py-3.5 shadow-[inset_0_0_0_1px_#2a2a30]', className)}>
      <div className="text-[12px] text-muted">{label}</div>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
        <span className="text-[26px] font-semibold leading-tight tracking-[-0.01em] text-fg">{value}</span>
        {delta && <span className="text-[12px]">{delta}</span>}
      </div>
      {sub && <div className="mt-1 text-[12px] leading-snug text-muted">{sub}</div>}
    </div>
  );
}

/** Горизонтальные полосы одного цвета: категории без порядка (тарифы) или этапы воронки. */
export function BarList({ rows, total }: { rows: { label: string; value: number; note?: string }[]; total: number }) {
  return (
    <div className="space-y-2.5">
      {rows.map((row) => (
        <div key={row.label}>
          <div className="mb-1 flex items-baseline justify-between gap-3 text-[14px]">
            <span className="text-fg">{row.label}</span>
            <span className="tabular-nums text-muted">
              <span className="font-semibold text-fg">{fmtInt(row.value)}</span> · {fmtPct(total ? row.value / total : 0)}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-[#2a2a30]">
            <div className="h-full rounded-full" style={{ width: `${total ? (row.value / total) * 100 : 0}%`, background: C1 }} />
          </div>
          {row.note && <div className="mt-0.5 text-[12px] text-muted">{row.note}</div>}
        </div>
      ))}
    </div>
  );
}

/** Малые доли — с десятыми: 2 из 456 это 0,4%, а не «0%». */
const share = (value: number) =>
  value > 0 && value < 0.1 ? `${(value * 100).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%` : fmtPct(value);

function Ratio({ label, part, whole, hint }: { label: string; part: number; whole: number; hint: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-line py-2.5 last:border-b-0">
      <div className="min-w-0">
        <div className="text-[14px] text-fg">{label}</div>
        <div className="text-[12px] text-muted">{hint}</div>
      </div>
      <div className="shrink-0 text-right">
        <div className="text-[17px] font-semibold tabular-nums text-fg">{whole ? share(part / whole) : '—'}</div>
        <div className="text-[12px] tabular-nums text-muted">
          {fmtInt(part)} / {fmtInt(whole)}
        </div>
      </div>
    </div>
  );
}

const sum = (rows: AnalyticsDay[], pick: (row: AnalyticsDay) => number | null) =>
  rows.reduce((total, row) => total + (pick(row) ?? 0), 0);

/** ₽ на слова и подборки: из всех расходов дня вычитаем игру и грамматику (старый бэкенд без полей — всё слова). */
const wordsRub = (row: AnalyticsDay) => Math.max(0, row.spendRub - (row.gameRub ?? 0) - (row.grammarRub ?? 0));

/** Вкладка админки (2026-10-21): «Аналитика» — ученики, активность, деньги; «Техника» — лимит, модель, качество. */
export type AdminPart = 'product' | 'tech';

export function AdminAnalyticsView({ data, part }: { data: AdminAnalytics; part: AdminPart }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'ru';
  const { kpis, series, days } = data;
  const dayList = series.map((row) => row.day);
  const col = (pick: (row: AnalyticsDay) => number | null) => series.map(pick);
  const chart = (kind: 'bar' | 'stacked' | 'line', list: ChartSeries[], format: (n: number) => string, label: string) => (
    <TimeChart
      days={dayList}
      series={list}
      kind={kind}
      format={format}
      lang={lang}
      ariaLabel={label}
      integer={format === fmtInt}
      area={kind === 'line'}
    />
  );
  const period = t('admin.analytics.period', { days });
  const unitsTracked = data.usageSince !== null;
  const unitsSince = data.usageSince && data.usageSince > dayList[0] ? fmtCalendarDay(data.usageSince, lang, 'long') : null;

  const tiles = [
    {
      part: 'product' as AdminPart,
      label: t('admin.analytics.kpi.users'),
      value: fmtInt(kpis.usersTotal),
      sub: t('admin.analytics.kpi.usersSub', { today: kpis.usersNewToday, period: kpis.usersNewPeriod, days }),
    },
    {
      part: 'product' as AdminPart,
      label: t('admin.analytics.kpi.activeToday'),
      value: fmtInt(kpis.activeToday),
      sub: t('admin.analytics.kpi.activeSub', { week: kpis.activeWeek, month: kpis.activeMonth }),
    },
    {
      part: 'product' as AdminPart,
      label: t('admin.analytics.kpi.activePeriod', { days }),
      value: fmtInt(kpis.activePeriod),
      delta: <Delta now={kpis.activePeriod} prev={kpis.activePrev} />,
      sub: t('admin.analytics.kpi.ofUsers', { pct: fmtPct(kpis.usersTotal ? kpis.activePeriod / kpis.usersTotal : 0) }),
    },
    {
      part: 'product' as AdminPart,
      label: t('admin.analytics.kpi.paying'),
      value: fmtInt(kpis.payingPro + kpis.payingPremium),
      sub: t('admin.analytics.kpi.payingSub', { pro: kpis.payingPro, max: kpis.payingPremium }),
    },
    {
      part: 'product' as AdminPart,
      label: t('admin.analytics.kpi.revenue', { days }),
      value: fmtRub(kpis.revenuePeriodRub),
      delta: <Delta now={kpis.revenuePeriodRub} prev={kpis.revenuePrevRub} />,
      sub: t('admin.analytics.kpi.revenueSub'),
    },
    {
      part: 'tech' as AdminPart,
      label: t('admin.analytics.kpi.units'),
      value: kpis.unitsToday === null ? '—' : fmtInt(kpis.unitsToday),
      sub: unitsTracked
        ? t('admin.analytics.kpi.unitsSub', { avg: kpis.unitsAvgDay ?? 0, total: kpis.unitsPeriod ?? 0 })
        : t('admin.analytics.unitsNotTracked'),
    },
    {
      part: 'tech' as AdminPart,
      label: t('admin.analytics.kpi.spend', { days }),
      value: fmtRub(kpis.spendPeriodRub),
      delta: <Delta now={kpis.spendPeriodRub} prev={kpis.spendPrevRub} goodUp={false} />,
      sub: t('admin.analytics.kpi.spendSub', { today: fmtRub(kpis.spendTodayRub), month: fmtRub(kpis.spendForecastMonthRub) }),
    },
    {
      part: 'tech' as AdminPart,
      label: t('admin.analytics.kpi.margin', { days }),
      value: fmtRub(kpis.marginPeriodRub),
      sub: t('admin.analytics.kpi.marginSub'),
    },
  ];

  const wordJobs = sum(series, (r) => r.cardJobs);
  const roundShare = (n: number) => `${fmtInt(n)} · ${fmtPct(wordJobs ? n / wordJobs : 0)}`;
  const plansTotal = data.plans.demo + data.plans.pro + data.plans.premium;
  const { quality } = data;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {tiles
          .filter((tile) => tile.part === part)
          .map(({ part: _part, ...tile }) => (
            <Tile key={tile.label} {...tile} />
          ))}
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        {part === 'product' && (
          <Panel title={t('admin.analytics.chart.newUsers')} hint={period}>
            <Legend
              className="mb-2"
              items={[
                { label: t('admin.analytics.series.verified'), color: C1, value: fmtInt(sum(series, (r) => r.newVerified)) },
                { label: t('admin.analytics.series.unverified'), color: C2, value: fmtInt(sum(series, (r) => r.newUsers - r.newVerified)) },
              ]}
            />
            {chart(
              'stacked',
              [
                { key: 'verified', label: t('admin.analytics.series.verified'), color: C1, values: col((r) => r.newVerified) },
                { key: 'unverified', label: t('admin.analytics.series.unverified'), color: C2, values: col((r) => r.newUsers - r.newVerified) },
              ],
              fmtInt,
              t('admin.analytics.chart.newUsers'),
            )}
          </Panel>
        )}

        {part === 'product' && (
          <Panel title={t('admin.analytics.chart.active')} hint={t('admin.analytics.activeHint')}>
            <Legend
              lines
              className="mb-2"
              items={[
                { label: t('admin.analytics.series.active'), color: C1 },
                { label: t('admin.analytics.series.learners'), color: C2 },
                { label: t('admin.analytics.series.creators'), color: C3 },
              ]}
            />
            {chart(
              'line',
              [
                { key: 'active', label: t('admin.analytics.series.active'), color: C1, values: col((r) => r.activeUsers) },
                { key: 'learners', label: t('admin.analytics.series.learners'), color: C2, values: col((r) => r.learners) },
                { key: 'creators', label: t('admin.analytics.series.creators'), color: C3, values: col((r) => r.creators) },
              ],
              fmtInt,
              t('admin.analytics.chart.active'),
            )}
          </Panel>
        )}

        {part === 'tech' && (
          <Panel
            title={t('admin.analytics.chart.units')}
            hint={
              !unitsTracked
                ? t('admin.analytics.unitsNotTracked')
                : unitsSince
                  ? t('admin.analytics.unitsSince', { date: unitsSince })
                  : t('admin.analytics.unitsTotal', { total: fmtInt(kpis.unitsPeriod ?? 0) })
            }
          >
            {chart(
              'bar',
              [{ key: 'units', label: t('admin.analytics.series.units'), color: C1, values: col((r) => r.units) }],
              fmtInt,
              t('admin.analytics.chart.units'),
            )}
          </Panel>
        )}

        {part === 'tech' && (
          <Panel
            title={t('admin.analytics.chart.rounds')}
            hint={t('admin.analytics.roundsHint', {
              words: t('admin.analytics.heatmap.words', { count: wordJobs }),
              topics: fmtInt(sum(series, (r) => r.topicJobs)),
            })}
          >
            <Legend
              className="mb-2"
              items={[
                { label: t('admin.analytics.series.roundOne'), color: R1, value: roundShare(sum(series, (r) => r.cardJobsOne)) },
                { label: t('admin.analytics.series.roundTwo'), color: R2, value: roundShare(sum(series, (r) => r.cardJobsTwo)) },
                {
                  label: t('admin.analytics.series.roundThree'),
                  color: R3,
                  value: roundShare(sum(series, (r) => r.cardJobsThreePlus)),
                },
              ]}
            />
            {chart(
              'stacked',
              [
                { key: 'one', label: t('admin.analytics.series.roundOne'), color: R1, values: col((r) => r.cardJobsOne) },
                { key: 'two', label: t('admin.analytics.series.roundTwo'), color: R2, values: col((r) => r.cardJobsTwo) },
                { key: 'three', label: t('admin.analytics.series.roundThree'), color: R3, values: col((r) => r.cardJobsThreePlus) },
              ],
              fmtInt,
              t('admin.analytics.chart.rounds'),
            )}
          </Panel>
        )}

        {part === 'tech' && (
          <Panel
            title={t('admin.analytics.chart.spend')}
            hint={t('admin.analytics.spendHint', {
              total: fmtRub(kpis.spendPeriodRub),
              avg: fmtRub(kpis.spendPeriodRub / days),
            })}
          >
            {/* Слова, игра и грамматика — стопкой (2026-11-01): ходы игры пишутся в тот же журнал модели. */}
            <Legend
              className="mb-2"
              items={[
                { label: t('admin.analytics.series.spendWords'), color: C1, value: fmtRub(sum(series, wordsRub)) },
                { label: t('admin.analytics.series.spendGame'), color: C2, value: fmtRub(sum(series, (r) => r.gameRub ?? 0)) },
                ...(sum(series, (r) => r.grammarRub ?? 0) > 0
                  ? [{ label: t('admin.analytics.series.spendGrammar'), color: C3, value: fmtRub(sum(series, (r) => r.grammarRub ?? 0)) }]
                  : []),
              ]}
            />
            {chart(
              'stacked',
              [
                { key: 'words', label: t('admin.analytics.series.spendWords'), color: C1, values: col(wordsRub) },
                { key: 'game', label: t('admin.analytics.series.spendGame'), color: C2, values: col((r) => r.gameRub ?? 0) },
                { key: 'grammar', label: t('admin.analytics.series.spendGrammar'), color: C3, values: col((r) => r.grammarRub ?? 0) },
              ],
              fmtRub,
              t('admin.analytics.chart.spend'),
            )}
          </Panel>
        )}

        {part === 'product' && (
          <Panel title={t('admin.analytics.chart.revenue')} hint={t('admin.analytics.revenueHint', { total: fmtRub(kpis.revenuePeriodRub) })}>
            {chart(
              'bar',
              [{ key: 'revenue', label: t('admin.analytics.series.revenue'), color: C1, values: col((r) => r.revenueRub) }],
              fmtRub,
              t('admin.analytics.chart.revenue'),
            )}
          </Panel>
        )}

        {part === 'product' && (
          <Panel title={t('admin.analytics.chart.cards')} hint={period}>
            <Legend
              className="mb-2"
              items={[
                { label: t('admin.analytics.series.cardsAi'), color: C1, value: fmtInt(sum(series, (r) => r.cardsAi)) },
                { label: t('admin.analytics.series.cardsManual'), color: C2, value: fmtInt(sum(series, (r) => r.cardsManual)) },
              ]}
            />
            {chart(
              'stacked',
              [
                { key: 'ai', label: t('admin.analytics.series.cardsAi'), color: C1, values: col((r) => r.cardsAi) },
                { key: 'manual', label: t('admin.analytics.series.cardsManual'), color: C2, values: col((r) => r.cardsManual) },
              ],
              fmtInt,
              t('admin.analytics.chart.cards'),
            )}
          </Panel>
        )}

        {part === 'product' && (
          <Panel title={t('admin.analytics.chart.sessions')} hint={period}>
            <Legend
              lines
              className="mb-2"
              items={[
                { label: t('admin.analytics.series.started'), color: C1, value: fmtInt(sum(series, (r) => r.sessionsStarted)) },
                { label: t('admin.analytics.series.completed'), color: C2, value: fmtInt(sum(series, (r) => r.sessionsCompleted)) },
              ]}
            />
            {chart(
              'line',
              [
                { key: 'started', label: t('admin.analytics.series.started'), color: C1, values: col((r) => r.sessionsStarted) },
                { key: 'completed', label: t('admin.analytics.series.completed'), color: C2, values: col((r) => r.sessionsCompleted) },
              ],
              fmtInt,
              t('admin.analytics.chart.sessions'),
            )}
          </Panel>
        )}
      </div>

      {part === 'tech' && (
        <Panel
          title={t('admin.analytics.heatmap.title')}
          hint={t('admin.analytics.heatmap.hint', { count: data.users.length, days })}
        >
          <UserDayHeatmap users={data.users} days={dayList} lang={lang} />
        </Panel>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        {part === 'product' && (
          <Panel title={t('admin.analytics.plans.title')} hint={t('admin.analytics.plans.hint', { total: fmtInt(plansTotal) })}>
            <BarList
              total={plansTotal}
              rows={[
                { label: 'Demo', value: data.plans.demo },
                { label: 'Pro', value: data.plans.pro },
                { label: 'Max', value: data.plans.premium },
              ]}
            />
          </Panel>
        )}
        {part === 'tech' && (
          <Panel title={t('admin.analytics.health.qualityTitle')}>
            <Ratio
              label={t('admin.analytics.health.failed')}
              hint={t('admin.analytics.health.failedHint', { days })}
              part={quality.cardsFailed}
              whole={quality.cardsCreated}
            />
            <Ratio
              label={t('admin.analytics.health.extra')}
              hint={t('admin.analytics.health.extraHint')}
              part={quality.extraCalls}
              whole={quality.calls}
            />
            <Ratio
              label={t('admin.analytics.health.topicFailed')}
              hint={t('admin.analytics.health.topicFailedHint', { days })}
              part={quality.topicFailed}
              whole={quality.topicBatches}
            />
          </Panel>
        )}
      </div>
    </div>
  );
}
